import {
  DEBUG_MODE,
  getStethoClientMediaToken,
  getStethoClientToken,
} from '../../../spdConfig';
import { Loader } from '../../loader';

export type VendorToken = string;
export type ClientToken = string;
export type DeviceToken = string;

export type TokenScope = 'client' | 'vendor';

export interface TokenResponse {
  scope: TokenScope;
  token: string;
}

// Ta
export class MiddlewareClient {
  public static BaseUrl = 'https://middleware.stethome.com';
  public static BaseMediaUrl = 'https://media-api.stethome.com';
  public static vendorToken = '';
  public static devMode = false;

  /// Te dwie funkcje powinny być używane po stronie klienta tylko do testów vendorToken jest tajny
  protected static async getClientTokenFromStetho(): Promise<ClientToken> {
    const resp = await fetch(`${MiddlewareClient.BaseUrl}/v2/token`, {
      method: 'get',
      headers: new Headers({
        Authorization: `Bearer ${this.vendorToken}`,
      }),
    });

    const body = (await resp.json()) as TokenResponse;

    return body.token;
  }

  protected static async getClientTokenFromCore(): Promise<ClientToken> {
    try {
      const json = await Loader.getSyncJSONWithVersion(
        getStethoClientToken(),
        null,
        true,
      );
      DEBUG_MODE && console.log('getClientTokenFromCore', json);
      return json.token;
    } catch (e) {
      console.error(e);
      return '';
    }
  }

  public static async getClientToken(): Promise<ClientToken> {
    if (this.devMode) {
      return MiddlewareClient.getClientTokenFromStetho();
    } else {
      return MiddlewareClient.getClientTokenFromCore();
    }
  }

  protected static async getClientMediaTokenFromStetho(): Promise<ClientToken | null> {
    console.log('getClientMediaToken');
    const response = await fetch(
      `${MiddlewareClient.BaseMediaUrl}/v2/security/token`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${this.vendorToken}`,
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json();
      // Pobieranie nagłówków `Request-ID` i `Debug-Token`
      const requestId = response.headers.get('Request-ID');
      const debugToken = response.headers.get('Debug-Token');

      console.error(
        'getClientMediaToken',
        'Request-ID',
        requestId,
        'Debug-Token',
        debugToken,
        'Status',
        response.statusText,
        'Error',
        errorData,
      );
      return null;
    }

    const data = await response.json();
    return data.token;
  }

  protected static async getClientMediaTokenFromCore(): Promise<ClientToken | null> {
    try {
      const json = await Loader.getSyncJSONWithVersion(
        getStethoClientMediaToken(),
        null,
        true,
      );
      DEBUG_MODE && console.log('getClientMediaTokenFromCore', json);
      return json.token;
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  public static async getClientMediaToken(): Promise<ClientToken | null> {
    if (this.devMode) {
      return MiddlewareClient.getClientMediaTokenFromStetho();
    } else {
      return MiddlewareClient.getClientMediaTokenFromCore();
    }
  }

  ///////
  public static async getVisitId(token: ClientToken): Promise<string | null> {
    const response = await fetch(
      `${MiddlewareClient.BaseUrl}/v2/compound/visit`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      },
    );

    if (!response.ok) {
      console.error('Error:', response.statusText);
      return null;
    }

    const jsonData = await response.json();
    const lastVisitId = jsonData.id;

    DEBUG_MODE && console.log('getVisitId', lastVisitId);

    return lastVisitId;
  }

  public static async postVisitData(
    visitId: string,
    token: ClientToken,
    position: number,
    audioData: ArrayBuffer,
  ) {
    const arrayBufferToString = (arrayBuffer: ArrayBuffer) => {
      const byteArray = new Uint8Array(arrayBuffer);
      let result = '';
      for (let i = 0; i < byteArray.length; i++) {
        result += String.fromCharCode(byteArray[i]);
      }
      return btoa(result);
    };

    const bodyData = {
      sync: true,
      meta: {
        clientId: '123545-sdk',
        device: {
          type: 'SM',
          firmware: '5.2.25',
          serial: '0000-0000-0000-0000-0000-0000',
        },
        patient: {
          age: 55.5,
          weight: 110.0,
          height: 191,
          sex: 'M',
        },
        ageGroup: 'adult-1',
        auscultationType: 'simple-asthma',
        custom: null,
      },
      files: [
        {
          encoding: 'mdcte',
          position: position,
          data: arrayBufferToString(audioData),
          pulmonary: true,
          cardiac: true,
        },
      ],
    };

    const response = await fetch(
      `${MiddlewareClient.BaseUrl}/v2/compound/visit/${visitId}`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bodyData),
      },
    );

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Error:', response.statusText, errorData);
      return;
    }

    const responseData = await response.json();
    DEBUG_MODE && console.log('postVisitData Response:', responseData);
  }

  public static async getDeviceAuth(
    token: ClientToken,
    deviceChallenge: string,
  ): Promise<DeviceToken> {
    DEBUG_MODE && console.log('getDeviceAuth', { token, deviceChallenge });

    const resp = await fetch(`${MiddlewareClient.BaseUrl}/v2/auth`, {
      method: 'post',
      headers: new Headers({
        Authorization: `Bearer ${token}`,
      }),
      body: btoa(deviceChallenge),
    });

    return atob(await resp.text());
  }

  public static async getVisitCheck(
    token: ClientToken,
    visitId: string,
    point: number,
  ) {
    const response = await fetch(
      `${MiddlewareClient.BaseUrl}/v2/compound/visit/${visitId}/recording/${point}/check`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json();
      // Pobieranie nagłówków `Request-ID` i `Debug-Token`
      const requestId = response.headers.get('Request-ID');
      const debugToken = response.headers.get('Debug-Token');
      console.error(
        'getVisitCheck',
        'Request-ID',
        requestId,
        'Debug-Token',
        debugToken,
        'Status',
        response.statusText,
        'Error',
        errorData,
      );

      return null;
    }

    const data = await response.json();
    DEBUG_MODE && console.log('getVisitCheck', data);
    return data;
  }

  public static async getSrcFrame(visitId: string, point: number, lang = 'pl') {
    const clientToken = await MiddlewareClient.getClientMediaToken();
    if (clientToken) {
      const src = MiddlewareClient.getSrcFrameWithToken(
        clientToken,
        visitId,
        point,
        lang,
      );
      return src;
    }
    return '';
  }

  protected static getSrcFrameWithToken(
    token: ClientToken,
    visitId: string,
    point: number,
    lang = 'pl',
  ): string {
    return `${MiddlewareClient.BaseMediaUrl}/v2/compound/frame/${visitId}/${point}/${lang}?details=2&bearer=${token}`;
  }
}
